import type { OrderDetailDto, OrderSummaryDto, ProductCardDto, StoreCardDto } from '@runnerboy/types';
import { Button, cx, Skeleton } from '@runnerboy/ui';
import { Globe2, MapPin, Search, Sparkles } from 'lucide-react';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AdBanner, AdStrip, AnnouncementTicker, FeatureBanner, HeroCarousel, PromoCarousel, RoadSafetyStrip, SponsoredProducts, useAds } from '../components/ads';
import { CategoryTile, ProductCard, StoreCard, useCartActions } from '../components/commerce';
import { SignatureHero } from '../components/hero';
import { Page, ResponsibleNote, SectionTitle, SeeAll } from '../components/shell';
import { client, DEMO_LOCATION, useAsync, useSession } from '../lib/session';

interface Category {
  slug: string;
  name: string;
  productType: string;
}

/** Tinted commerce bands: visual variety between rails instead of identical white cards. */
const BANDS: { key: string; title: string; eyebrow: string; categories: string[]; style: CSSProperties }[] = [
  {
    key: 'mixers',
    title: 'Mixers & soda',
    eyebrow: 'Pour it right',
    categories: ['mixers', 'soda', 'tonic', 'soft-drinks'],
    style: { background: 'radial-gradient(120% 120% at 100% 0%, #FFE2BD 0%, transparent 55%), linear-gradient(160deg, #FFF4E6 0%, #FDE7CC 100%)' },
  },
  {
    key: 'water',
    title: 'Water & ice',
    eyebrow: 'Stay hydrated',
    categories: ['water', 'ice'],
    style: { background: 'radial-gradient(120% 120% at 0% 0%, #CFE7FF 0%, transparent 55%), linear-gradient(160deg, #EEF6FF 0%, #DCEBFB 100%)' },
  },
  {
    key: 'snacks',
    title: 'Snacks',
    eyebrow: 'Something to munch',
    categories: ['snacks', 'namkeen'],
    style: { background: 'radial-gradient(120% 120% at 100% 100%, #F6D6A8 0%, transparent 55%), linear-gradient(160deg, #FFF7EC 0%, #F8E8D2 100%)' },
  },
];

function useRotating(items: string[], ms = 2600) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (items.length < 2) return;
    const id = setInterval(() => setI((x) => (x + 1) % items.length), ms);
    return () => clearInterval(id);
  }, [items.length, ms]);
  return items[i % Math.max(1, items.length)] ?? '';
}

export function HomePage({ onChooseLocation }: { onChooseLocation: () => void }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { location, availability, availabilityLoading, availabilityError, storeId, setStoreId, user, ageGate, config, setLocation } = useSession();
  const outside = availabilityError === 'OUTSIDE_INDIA';
  const stores = useAsync((s) => client.get<StoreCardDto[]>(`/public/stores?lat=${location?.lat}&lng=${location?.lng}`, s), [location?.lat, location?.lng], {
    enabled: !!location && !outside,
  });
  const categories = useAsync((s) => client.get<Category[]>('/public/categories', s), []);
  const selected = stores.data?.find((s) => s.id === storeId) ?? stores.data?.[0] ?? null;
  const ads = useAds(['HOME_HERO', 'HOME_CAROUSEL', 'HOME_BANNER', 'HOME_STRIP', 'ANNOUNCEMENT_TICKER', 'SPONSORED_PRODUCTS', 'ROAD_SAFETY_STRIP', 'INFO_BANNER'], {
    storeId: selected?.id,
    enabled: !!location,
  });
  const suggestions = ageGate === 'confirmed' ? ['chilled beer', 'single malt', 'tonic water', 'party ice', 'masala peanuts'] : ['tonic water', 'party ice', 'soda', 'masala peanuts'];
  const hint = useRotating(suggestions);

  if (!location) return <Welcome onChooseLocation={onChooseLocation} />;

  const visibleCategories = (categories.data ?? []).filter((c) => ageGate === 'confirmed' || c.productType !== 'ALCOHOL');
  // The signature hero carries the road-safety message; the carousel is for commercial heroes.
  const commercialHeroes = (ads.HOME_HERO ?? []).filter((a) => a.artKey !== 'road-night');

  return (
    <>
      {/* Dark top zone continues the header. */}
      <div className="bg-night-900 pb-12">
        <div className="mx-auto max-w-3xl px-4 pt-1">
          <button
            onClick={() => nav('/search')}
            className="rb-press flex h-[54px] w-full items-center gap-3 rounded-full bg-white pl-4 pr-2 text-left shadow-[0_10px_30px_-10px_rgb(0_0_0/0.6)]"
            aria-label={t('common.search')}
          >
            <Search className="h-5 w-5 shrink-0 text-ink-900" strokeWidth={2.4} aria-hidden />
            <span className="min-w-0 flex-1 truncate text-[15px] text-ink-400">
              {t('home.searchFor', 'Search')} <span key={hint} className="inline-block animate-[fade-in_300ms_ease-out] font-medium text-ink-700">“{hint}”</span>
            </span>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink-900 text-marigold-500" aria-hidden>
              <Sparkles className="h-[18px] w-[18px]" />
            </span>
          </button>

          <div className="mt-4">
            <SignatureHero />
          </div>

          <div className="mt-4">
            {outside ? null : availabilityLoading ? (
              <Skeleton className="h-9 rounded-full bg-white/10" />
            ) : availability && availability.reasons.length > 0 ? (
              <div className="rounded-[18px] bg-white/[0.06] p-3.5 text-[13px] leading-relaxed text-white/85 ring-1 ring-white/10">
                <div className="rb-eyebrow mb-1 text-[10px] text-marigold-500">{availability.jurisdiction?.cityName ?? availability.jurisdiction?.stateName ?? t('location.choose')}</div>
                {availability.reasons.map((r) => t(`compliance.${r.code}`, { defaultValue: r.message })).join(' ')}
                {config?.testMode && !availability.jurisdiction?.isDemo && (
                  <button className="mt-2 block font-semibold text-marigold-500 underline underline-offset-2" onClick={() => setLocation(DEMO_LOCATION)}>
                    {t('home.tryDemo', 'Explore the demo city instead')}
                  </button>
                )}
              </div>
            ) : availability?.alcoholDeliveryAvailableNow ? (
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-white">
                  <span className="relative flex h-2.5 w-2.5" aria-hidden>
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-500 opacity-60" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success-500" />
                  </span>
                  {t('location.available')} · {availability.jurisdiction?.cityName}
                </span>
                {selected && <span className="rb-num text-[12px] text-white/60">{t('home.etaFrom', 'from {{min}} min', { min: selected.etaMinutes })}</span>}
              </div>
            ) : null}
          </div>
          {ads.ANNOUNCEMENT_TICKER && ads.ANNOUNCEMENT_TICKER.length > 0 && (
            <div className="mt-4">
              <AnnouncementTicker ads={ads.ANNOUNCEMENT_TICKER} dark />
            </div>
          )}
        </div>
      </div>

      <Page className="relative -mt-8 rounded-t-[32px] bg-paper pt-6">
        {outside && <OutsideIndia testMode={!!config?.testMode} onDemo={() => setLocation(DEMO_LOCATION)} onChoose={onChooseLocation} />}

        {selected && visibleCategories.length > 0 && (
          <>
            <SectionTitle className="mt-1" eyebrow={t('home.aisles', 'Aisles')} action={<SeeAll to={`/store/${selected.id}`} label={t('common.seeAll', 'See all')} />}>
              {t('home.categories')}
            </SectionTitle>
            <div className="grid grid-cols-4 gap-x-3 gap-y-4">
              {visibleCategories.slice(0, 8).map((c) => (
                <CategoryTile key={c.slug} slug={c.slug} name={c.name} to={`/store/${selected.id}?category=${c.slug}`} />
              ))}
            </div>
          </>
        )}

        {!outside && (
          <>
            <SectionTitle eyebrow={stores.data ? t('home.storeCount', '{{count}} licensed nearby', { count: stores.data.length }) : undefined}>{t('home.storesNear')}</SectionTitle>
            {stores.loading ? (
              <div className="flex gap-3 overflow-hidden">
                {[0, 1].map((i) => (
                  <Skeleton key={i} className="h-[214px] w-[282px] shrink-0 rounded-[24px]" />
                ))}
              </div>
            ) : (stores.data ?? []).length === 0 ? (
              <div className="rounded-[24px] bg-white p-6 text-center shadow-[var(--shadow-card)]">
                <div className="rb-display text-[19px] font-bold text-ink-900">{t('home.noStores')}</div>
                <p className="mt-1 text-sm text-ink-500">{t('home.noStoresBody')}</p>
                {config?.testMode && (
                  <Button className="mt-4" variant="secondary" onClick={() => setLocation(DEMO_LOCATION)}>
                    {t('home.tryDemo', 'Explore the demo city instead')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="rb-snap-x -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
                {(stores.data ?? []).map((s) => (
                  <StoreCard
                    key={s.id}
                    store={s}
                    selected={selected?.id === s.id}
                    onSelect={() => {
                      setStoreId(s.id);
                      nav(`/store/${s.id}`);
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {commercialHeroes.length > 0 && (
          <>
            <SectionTitle eyebrow={t('home.spotlight', 'Spotlight')}>{t('home.featured', 'Featured')}</SectionTitle>
            <HeroCarousel ads={commercialHeroes} />
          </>
        )}

        {ads.HOME_STRIP?.[0] && (
          <div className="mt-6">
            <AdStrip ad={ads.HOME_STRIP[0]} />
          </div>
        )}

        {selected && <PopularRow storeId={selected.id} storeName={selected.name.split(' — ')[0] ?? selected.name} />}

        {(ads.HOME_CAROUSEL?.length ?? 0) > 0 && <PromoCarousel ads={ads.HOME_CAROUSEL ?? []} title={<SectionTitle eyebrow={t('home.offers', 'Offers & picks')}>{t('home.forTonight', 'For tonight')}</SectionTitle>} />}

        {selected && <Band band={BANDS[0]!} storeId={selected.id} />}

        {ads.HOME_BANNER?.[0] && (
          <div className="mt-8">
            <AdBanner ad={ads.HOME_BANNER[0]} />
          </div>
        )}

        {selected && <Band band={BANDS[1]!} storeId={selected.id} />}
        {selected && <Band band={BANDS[2]!} storeId={selected.id} />}

        {ads.SPONSORED_PRODUCTS?.[0] && selected && (
          <div className="mt-8">
            <SponsoredProducts ad={ads.SPONSORED_PRODUCTS[0]} storeId={selected.id} />
          </div>
        )}

        {user && selected && <OrderAgainRow storeId={selected.id} />}

        {(ads.INFO_BANNER?.[0] || ads.ROAD_SAFETY_STRIP?.[0]) && (
          <div className="mt-9 flex flex-col gap-3">
            {ads.INFO_BANNER?.[0] && <FeatureBanner ad={ads.INFO_BANNER[0]} />}
            {ads.ROAD_SAFETY_STRIP?.[0] && <RoadSafetyStrip ad={ads.ROAD_SAFETY_STRIP[0]} />}
          </div>
        )}

        <ResponsibleNote />
      </Page>
    </>
  );
}

function ProductRail({ items, loading }: { items: ProductCardDto[] | undefined; loading: boolean }) {
  const actions = useCartActions();
  if (loading)
    return (
      <div className="flex gap-3 overflow-hidden">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-[276px] w-[156px] shrink-0 rounded-[22px]" />
        ))}
      </div>
    );
  if (!items || items.length === 0) return null;
  return (
    <div className="rb-snap-x -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
      {items.map((p) => (
        <div key={p.storeProductId} className="w-[156px] shrink-0">
          <ProductCard product={p} actions={actions} compact />
        </div>
      ))}
    </div>
  );
}

function PopularRow({ storeId, storeName }: { storeId: string; storeName: string }) {
  const { t } = useTranslation();
  const r = useAsync((s) => client.get<{ items: ProductCardDto[] }>(`/public/stores/${storeId}/catalog?sort=popular&limit=12`, s), [storeId]);
  if (!r.loading && (r.data?.items.length ?? 0) === 0) return null;
  return (
    <>
      <SectionTitle eyebrow={t('home.bestSellers', 'Best sellers')} action={<SeeAll to={`/store/${storeId}`} label={t('common.seeAll', 'See all')} />}>
        {t('home.popular', { store: storeName, defaultValue: 'Popular right now' })}
      </SectionTitle>
      <ProductRail items={r.data?.items} loading={r.loading} />
    </>
  );
}

function Band({ band, storeId }: { band: (typeof BANDS)[number]; storeId: string }) {
  const { t } = useTranslation();
  const [cat, setCat] = useState(band.categories[0]!);
  const r = useAsync((s) => client.get<{ items: ProductCardDto[] }>(`/public/stores/${storeId}/catalog?category=${cat}&limit=12`, s), [storeId, cat]);
  return (
    <section className="-mx-4 mt-9 px-4 py-5" style={band.style}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="rb-eyebrow text-[10px] text-ink-500">{t(`home.band.${band.key}.eyebrow`, band.eyebrow)}</div>
          <h2 className="rb-display mt-1 text-[22px] font-bold text-ink-900">{t(`home.band.${band.key}.title`, band.title)}</h2>
        </div>
        <SeeAll to={`/store/${storeId}?category=${cat}`} label={t('common.seeAll', 'See all')} />
      </div>
      {band.categories.length > 1 && (
        <div className="rb-scroll-x -mx-4 mt-3 flex gap-2 overflow-x-auto px-4" role="tablist">
          {band.categories.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={cat === c}
              onClick={() => setCat(c)}
              className={cx('rb-press h-9 shrink-0 rounded-full px-4 text-[13px] font-semibold capitalize transition-colors', cat === c ? 'bg-ink-900 text-white' : 'bg-white/80 text-ink-700 shadow-[var(--shadow-card)]')}
            >
              {t(`home.acc.${c}`, c.replace('-', ' '))}
            </button>
          ))}
        </div>
      )}
      <div className="mt-3.5">
        {!r.loading && (r.data?.items.length ?? 0) === 0 ? <p className="py-6 text-center text-sm text-ink-500">{t('home.nothingHere', 'Nothing in this aisle right now.')}</p> : <ProductRail items={r.data?.items} loading={r.loading} />}
      </div>
    </section>
  );
}

/** Items from the customer's recent delivered orders at this store, shown with today's price and stock. */
function OrderAgainRow({ storeId }: { storeId: string }) {
  const { t } = useTranslation();
  const r = useAsync(
    async (s) => {
      const list = await client.get<{ items: OrderSummaryDto[] }>('/orders', s);
      const recent = list.items.filter((o) => o.storeId === storeId && o.status === 'DELIVERED').slice(0, 2);
      const details = await Promise.all(recent.map((o) => client.get<OrderDetailDto>(`/orders/${o.id}`, s)));
      const ids = [...new Set(details.flatMap((d) => d.items.map((i) => i.productId)))].slice(0, 12);
      if (ids.length === 0) return { items: [] as ProductCardDto[] };
      return client.get<{ items: ProductCardDto[] }>(`/public/stores/${storeId}/catalog?ids=${ids.join(',')}&limit=12`, s);
    },
    [storeId],
  );
  if (!r.loading && (r.data?.items.length ?? 0) === 0) return null;
  return (
    <>
      <SectionTitle eyebrow={t('home.fromYourOrders', 'From your orders')} action={<SeeAll to="/orders" label={t('nav.orders')} />}>
        {t('home.orderAgain', 'Order again')}
      </SectionTitle>
      <ProductRail items={r.data?.items} loading={r.loading} />
    </>
  );
}

function OutsideIndia({ testMode, onDemo, onChoose }: { testMode: boolean; onDemo: () => void; onChoose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[26px] bg-white p-5 shadow-[var(--shadow-card)]">
      <div className="flex items-start gap-3">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-[16px] bg-marigold-100 text-marigold-700">
          <Globe2 className="h-6 w-6" aria-hidden />
        </span>
        <div>
          <div className="rb-display text-[19px] font-bold leading-tight text-ink-900">{t('location.outsideIndia')}</div>
          <div className="mt-1 text-sm text-ink-500">{t('home.outsideBody', 'Choose an address in a city we serve.')}</div>
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-2">
        {testMode && (
          <Button block size="lg" variant="accent" onClick={onDemo}>
            <Sparkles className="h-5 w-5" aria-hidden /> {t('home.tryDemo', 'Explore the demo city instead')}
          </Button>
        )}
        <Button block size="lg" variant="secondary" onClick={onChoose}>
          <MapPin className="h-5 w-5" aria-hidden /> {t('location.choose')}
        </Button>
      </div>
    </div>
  );
}

/** First screen before a location is chosen. */
function Welcome({ onChooseLocation }: { onChooseLocation: () => void }) {
  const { t } = useTranslation();
  const { config, setLocation } = useSession();
  return (
    <div className="min-h-[calc(100dvh-80px)] bg-night-900 pb-40">
      {/* Night backdrop for the whole viewport, so no ivory shows below short content or behind the nav. */}
      <div className="fixed inset-0 -z-10 bg-night-900" aria-hidden />
      <div className="mx-auto max-w-3xl px-4 pt-2">
        <SignatureHero linked={false} />
        <Point>{t('home.welcomeBody', 'Licensed neighbourhood stores, verified runners and an ID check at your door.')}</Point>
        <div className="mt-6 flex flex-col gap-2.5">
          <Button size="lg" block variant="accent" onClick={onChooseLocation}>
            <MapPin className="h-5 w-5" aria-hidden /> {t('location.choose')}
          </Button>
          {config?.testMode && (
            <Button size="lg" block variant="glass" onClick={() => setLocation(DEMO_LOCATION)}>
              <Sparkles className="h-5 w-5 text-marigold-500" aria-hidden /> {t('home.tryDemo', 'Explore the demo city instead')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Point({ children }: { children: ReactNode }) {
  return <p className="mt-6 text-center text-[15px] leading-relaxed text-white/75">{children}</p>;
}
